import { useCallback } from "react";
import type { BaseEntity } from "../../types";

interface UseSelectionCallbacksProps<TData extends BaseEntity> {
  selectedItems: Set<string>;
  setSelectedItems: React.Dispatch<React.SetStateAction<Set<string>>>;
  onBulkAction?: (actionId: string, selectedItems: TData[]) => void;
}

export function useSelectionCallbacks<TData extends BaseEntity>({
  selectedItems,
  setSelectedItems,
  onBulkAction,
}: UseSelectionCallbacksProps<TData>) {
  const handleSelectItem = useCallback(
    (id: string, selected: boolean) => {
      setSelectedItems((prev) => {
        const newSet = new Set(prev);
        if (selected) {
          newSet.add(id);
        } else {
          newSet.delete(id);
        }
        return newSet;
      });
    },
    [setSelectedItems]
  );

  const createHandleSelectAll = useCallback(() => {
    return (selected: boolean, paginatedData: TData[]) => {
      if (selected) {
        setSelectedItems(new Set(paginatedData.map((item) => item.id)));
      } else {
        setSelectedItems(new Set());
      }
    };
  }, [setSelectedItems]);

  const createHandleBulkAction = useCallback(() => {
    return (actionId: string, paginatedData: TData[]) => {
      const selectedItemsList = paginatedData.filter((item) => selectedItems.has(item.id));
      onBulkAction?.(actionId, selectedItemsList);
      setSelectedItems(new Set());
    };
  }, [selectedItems, onBulkAction, setSelectedItems]);

  return {
    handleSelectItem,
    createHandleSelectAll,
    createHandleBulkAction,
  };
}
